import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Loader2, ShieldCheck } from 'lucide-react';
import Layout from '../components/Layout';
import Modal from '../components/Modal';
import { useScreenNavigation } from '../hooks/useScreenNavigation';
import { logger } from '../utils/logger';
import { useClaimStore } from '../store/claimStore';
import { getScreenAfterLegalNotice } from '../utils/eventDateScreenVisibility';

/**
 * Legal Notice Screen (00-02-AVIS)
 *
 * Displays important legal notice about automated decision-making and AI processing.
 * User must consent via checkbox before continuing to the claim process.
 *
 * Navigation:
 * - Cancel: Returns to Home (00-00-ACCUEIL)
 * - Continue: Proceeds to the Event date page (00-03-EVENEMENT) when the checkbox is
 *   checked, or straight to the Claimant page (01-00-DEMANDEUR) when the event date
 *   is already known / the draft predates it. This is the FORWARD half of the
 *   `utils/eventDateScreenVisibility` seam — the BACKWARD half lives in
 *   `ContactPreferencesScreen.handleBack` and must stay consistent with it (DEV-7682).
 */
const SCREEN_CODE = '00-02-AVIS';

const LegalNoticeScreen: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  useScreenNavigation(SCREEN_CODE);

  const [consentChecked, setConsentChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState(false);

  const handleCancel = () => {
    useClaimStore.getState().clearClaim();
    navigate('/');
  };

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!consentChecked) {
      return;
    }

    setIsSubmitting(true);
    setImportError(false);

    try {
      // Store consent timestamp for audit purposes
      const consentRecord = {
        timestamp: new Date().toISOString(),
        screenCode: '00-02-AVIS',
        consentGiven: true,
        userAgent: navigator.userAgent,
      };

      localStorage.setItem('legalNoticeConsent', JSON.stringify(consentRecord));

      const existingEventNumber = useClaimStore.getState().existingEventNumber;
      if (existingEventNumber) {
        setIsImporting(true);
        const claimCreated = await useClaimStore.getState().createNewClaimOnExistingEvent();
        if (!claimCreated) {
          setImportError(true);
          return; // stay on consent; stash retained; no auto-retry
        }
      }

      // 00-03-EVENEMENT, or 01-00-DEMANDEUR when the shared predicate skips it.
      navigate(getScreenAfterLegalNotice(useClaimStore.getState().stepData), {
        state: location.state
      });
    } catch (error) {
      logger.error('Error submitting consent', {
        errorType: 'ConsentSubmitError',
        screen: 'LegalNoticeScreen',
      }, error as Error);
      setImportError(true);
    } finally {
      setIsImporting(false);
      setIsSubmitting(false);
    }
  };

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Layout showProgress={false}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <ShieldCheck className="w-16 h-16 text-tenant-600 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t('legalNotice.title')}
          </h1>
        </div>

          <form onSubmit={handleContinue} className="space-y-6">
            {/* Legal Notice Content */}
            <div className="bg-tenant-50 border border-tenant-200 rounded-lg p-6">
              <div>
                <div className="prose prose-blue max-w-none">
                    {/* Engagement, autorisation et subrogation */}
                    <h3 className="font-semibold text-gray-900 mb-3">
                      {t('legalNotice.engagement.title')}
                    </h3>

                    {[1, 2, 3, 4, 5, 6, 7].map((num) => (
                      <p key={num} className="text-gray-700 leading-relaxed mb-4">
                        <strong>{num}.</strong> {t(`legalNotice.engagement.paragraph${num}`)}
                      </p>
                    ))}

                    {/* Personal Information Processing */}
                    <h3 className="font-semibold text-gray-900 mb-3 mt-6">
                      {t('legalNotice.personalInformation.title')}
                    </h3>

                    {/* Consent Checkbox */}
                    <div className="my-4 bg-white border border-gray-300 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          id="legal-consent"
                          checked={consentChecked}
                          onChange={(e) => setConsentChecked(e.target.checked)}
                          className="mt-1 h-5 w-5 text-tenant-600 focus:ring-tenant-500 border-gray-300 rounded cursor-pointer"
                          required
                          aria-required="true"
                          aria-label={t('legalNotice.personalInformation.consentText')}
                        />
                        <label
                          htmlFor="legal-consent"
                          className="text-gray-900 cursor-pointer select-none flex-1"
                        >
                          <span className="font-medium">
                            {t('legalNotice.personalInformation.consentText')}{' '}
                            <a
                              href={t('legalNotice.privacyPolicyUrl')}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-tenant-600 hover:text-tenant-800 underline"
                            >
                              {t('legalNotice.privacyPolicyText')}
                            </a>.
                          </span>
                          <span className="text-red-600 ml-1" aria-label="required">*</span>
                        </label>
                      </div>
                    </div>

                    <p className="text-gray-700 leading-relaxed mb-4">
                      {t('legalNotice.personalInformation.paragraph1')}
                    </p>
                  </div>
              </div>
            </div>

            {/* Validation Message */}
            {!consentChecked && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-yellow-800 text-sm">
                  <AlertCircle className="w-4 h-4 inline mr-2" />
                  {t('legalNotice.validationMessage')}
                </p>
              </div>
            )}

            {/* Import error (existing-event flow only) */}
            {importError && (
              <p role="alert" className="text-red-600 mt-2">{t('legalNotice.importError')}</p>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 pt-6">
              <button
                type="button"
                onClick={handleCancel}
                className="flex-1 py-3 px-6 border border-gray-300 rounded-lg font-semibold text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                aria-label={t('legalNotice.cancelButton')}
              >
                {t('legalNotice.cancelButton')}
              </button>

              <button
                type="submit"
                disabled={!consentChecked || isSubmitting}
                className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-colors ${
                  consentChecked && !isSubmitting
                    ? 'bg-tenant-600 text-white hover:bg-tenant-700 shadow-md'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                aria-label={t('legalNotice.continueButton')}
                aria-disabled={!consentChecked || isSubmitting}
              >
                {isSubmitting
                  ? t('legalNotice.processing')
                  : t('legalNotice.continueButton')
                }
              </button>
            </div>

            {/* Required Field Notice */}
            <div className="text-center text-sm text-gray-500 pt-2">
              <span className="text-red-600">*</span> {t('legalNotice.requiredFieldNotice')}
            </div>
          </form>

        {/* Footer Information */}
        <div className="mt-8 pt-6 border-t border-gray-200 text-center text-sm text-gray-500">
          <p>
            {t('legalNotice.versionInfo', {
              date: new Date().toLocaleDateString(t('legalNotice.locale'))
            })}
          </p>
        </div>
      </div>
      <Modal
        isOpen={isImporting}
        onClose={() => { /* non-dismissible loading modal */ }}
        title={t('legalNotice.importingTitle')}
        dismissible={false}
        showCloseButton={false}
        size="sm"
      >
        <div className="flex flex-col items-center justify-center py-6 text-center">
          <Loader2 className="w-16 h-16 text-blue-600 animate-spin mb-4" />
          <p className="text-lg font-medium text-gray-800">{t('legalNotice.importingMessage')}</p>
          <p className="text-sm text-gray-500 mt-1">{t('legalNotice.importingSubMessage')}</p>
        </div>
      </Modal>
    </Layout>
  );
};

export default LegalNoticeScreen;
